import {
  safeRemoveLocalStorage,
  safeGetLocalStorageJson,
  safeSetLocalStorage,
} from './safeLocalStorage';
import type {
  ThemeChannel,
  ThemeMode,
  ThemeName,
} from 'foundations-theming-base/types';
import {
  getPortalId,
  LOCAL_STORAGE_THEME_KEY as EXTERNAL_LOCAL_STORAGE_KEY,
  HUBLESS_LOCAL_STORAGE_THEME_KEY as EXTERNAL_HUBLESS_LOCAL_STORAGE_KEY,
} from 'foundations-theming-base/setup-theme';
import { LOCAL_STORAGE_THEME_KEY } from './getLocalThemeSettings';
import { getHostname } from './utils';

export type { ThemeChannel };

type UserBetaState = 'opting-in';
type UserThemeCookieValue = {
  channel?: ThemeChannel;
  state?: UserBetaState;
};

type UserThemeCookieOptions =
  | { maxAge: number; channel: ThemeChannel; state?: UserBetaState }
  | { maxAge: number; channel?: undefined; state?: never };

export const USER_THEME_COOKIE_NAME = 'hs-user-theme';

const getCookieDomain = () => {
  const hostname = getHostname();
  const isHubspotQaHost =
    hostname === 'hubspotqa.com' || hostname.endsWith('.hubspotqa.com');
  return isHubspotQaHost ? '.hubspotqa.com' : '.hubspot.com';
};

// Retained for future theme-channel work; not currently called.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const setUserThemeCookie = ({
  maxAge,
  channel,
  state,
}: UserThemeCookieOptions) => {
  const value: UserThemeCookieValue = state ? { channel, state } : { channel };
  const valueStr =
    channel === undefined ? '' : `${encodeURIComponent(JSON.stringify(value))}`;

  document.cookie = [
    `${USER_THEME_COOKIE_NAME}=${valueStr}`,
    'path=/',
    `domain=${getCookieDomain()}`,
    `max-age=${maxAge}`,
    'secure',
    'SameSite=Lax',
  ].join('; ');
};

export const broadcastThemeChange = (themeKey?: string) => {
  try {
    const reloadEvent =
      themeKey === undefined
        ? { type: 'trellis:theme' }
        : { type: 'trellis:theme', themeKey };
    window.postMessage(reloadEvent);
    for (let i = 0; i < window.frames.length; i++) {
      if (typeof window.frames[i].postMessage === 'function') {
        window.frames[i].postMessage(reloadEvent);
      }
    }
  } catch (e) {
    console.error('Error updating trellis theme, try reloading the page.');
  }
};

export const removeDevOnlyThemeSetting = () => {
  safeRemoveLocalStorage(LOCAL_STORAGE_THEME_KEY);
};

export const removeThemeSetting = () => {
  const portalId = getPortalId();
  if (!portalId) {
    console.warn('No portal ID found, cannot change theme setting.');
    return;
  }

  const currentThemeSetting = safeGetLocalStorageJson(
    EXTERNAL_LOCAL_STORAGE_KEY
  );

  if (!currentThemeSetting?.[portalId]) {
    return;
  }
  const newThemeSetting = { ...currentThemeSetting };
  delete newThemeSetting[portalId];

  if (Object.keys(newThemeSetting).length === 0) {
    safeRemoveLocalStorage(EXTERNAL_LOCAL_STORAGE_KEY);
  } else {
    safeSetLocalStorage(
      EXTERNAL_LOCAL_STORAGE_KEY,
      JSON.stringify(newThemeSetting)
    );
  }
};

export const removeHublessThemeSetting = () => {
  safeRemoveLocalStorage(EXTERNAL_HUBLESS_LOCAL_STORAGE_KEY);
};

/**
 * Changes the customers theme.
 * Not read directly by ThemeProvider but rather used indirectly to set `html` data-attribute.
 */
export const changeThemeSetting = (
  themeName: ThemeName,
  themeMode: ThemeMode
) => {
  const portalId = getPortalId();
  if (!portalId) {
    console.warn('No portal ID found, cannot change theme setting.');
    return;
  }

  const currentThemeSetting = safeGetLocalStorageJson(
    EXTERNAL_LOCAL_STORAGE_KEY
  );

  const newThemeSetting = {
    ...currentThemeSetting,
    [portalId]: `${themeName}-${themeMode}`,
  };

  safeSetLocalStorage(
    EXTERNAL_LOCAL_STORAGE_KEY,
    JSON.stringify(newThemeSetting)
  );
};

export const changeHublessThemeSetting = (
  themeName: ThemeName,
  themeMode: ThemeMode
) => {
  const newThemeSetting = `${themeName}-${themeMode}`;
  safeSetLocalStorage(EXTERNAL_HUBLESS_LOCAL_STORAGE_KEY, newThemeSetting);
};

export const changeThemeSettingWithLiveUpdate = (
  themeName: ThemeName,
  themeMode: ThemeMode
) => {
  changeThemeSetting(themeName, themeMode);
  broadcastThemeChange();
};

const INTERNAL_LOCAL_STORAGE_KEY = 'trellis:theme';
/**
 * @deprecated This changes the theme setting directly in local storage. Do not use this in new code.
 */
export const changeTheme = (themeName: ThemeName, themeMode: ThemeMode) => {
  safeSetLocalStorage(INTERNAL_LOCAL_STORAGE_KEY, `${themeName}-${themeMode}`);
};

/**
 * @deprecated This changes the theme setting directly in local storage. Do not use this in new code.
 */
export const changeThemeWithLiveUpdate = (
  themeName: ThemeName,
  themeMode: ThemeMode
) => {
  changeTheme(themeName, themeMode);
  broadcastThemeChange();
};
